import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import {
  platformSaasBillingCatalogService,
  platformSaasBillingSubscriptionService,
} from '../../services/api';

const INITIAL_STATUS_OPTIONS = [
  { label: 'trialing', value: 'trialing' },
  { label: 'active', value: 'active' },
];

const STATUS_CHANGE_OPTIONS = [
  { label: 'active', value: 'active' },
  { label: 'grace', value: 'grace' },
  { label: 'suspended', value: 'suspended' },
  { label: 'cancelled', value: 'cancelled' },
];

const SUBSCRIPTION_STATUS_TAG_COLOR = {
  trialing: 'blue',
  active: 'green',
  grace: 'gold',
  suspended: 'orange',
  cancelled: 'red',
};

const USAGE_STATUS_TAG_COLOR = {
  ok: 'green',
  warning: 'gold',
  exceeded: 'red',
  unlimited: 'blue',
  enabled: 'green',
  disabled: 'default',
  no_subscription: 'default',
};

const LIFECYCLE_STATUS_TAG_COLOR = {
  trialing: 'blue',
  active: 'green',
  grace: 'gold',
  suspended: 'red',
  cancelled: 'default',
  no_subscription: 'default',
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('pt-BR');
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleDateString('pt-BR');
};

const getErrorMessage = (requestError, fallbackMessage) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallbackMessage;
};

const parseDateForForm = (value) => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const serializeDateForApi = (value, fieldName) => {
  if (!value) {
    return null;
  }

  if (!dayjs.isDayjs(value) || !value.isValid()) {
    throw new Error(`Data invalida para ${fieldName}.`);
  }

  return value.toDate().toISOString();
};

const renderSubscriptionStatusTag = (status) => {
  const normalized = String(status || '').toLowerCase();
  const color = SUBSCRIPTION_STATUS_TAG_COLOR[normalized] || 'default';
  const label = normalized || '-';
  return <Tag color={color}>{label}</Tag>;
};

const PlatformTenantSubscriptionPanel = ({ tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);
  const [plansLoadFailed, setPlansLoadFailed] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [effectiveEntitlements, setEffectiveEntitlements] = useState([]);
  const [usageMetrics, setUsageMetrics] = useState([]);
  const [lifecycleState, setLifecycleState] = useState(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [datesModalOpen, setDatesModalOpen] = useState(false);

  const [creating, setCreating] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [updatingDates, setUpdatingDates] = useState(false);

  const [createForm] = Form.useForm();
  const [changePlanForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [datesForm] = Form.useForm();

  const loadAllData = useCallback(async () => {
    if (!tenantId) {
      setError('Tenant invalido para assinatura SaaS.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const requests = [
      ['plans', platformSaasBillingCatalogService.listPlans()],
      ['current', platformSaasBillingSubscriptionService.getTenantCurrentSubscription(tenantId)],
      ['history', platformSaasBillingSubscriptionService.listTenantSubscriptions(tenantId)],
      ['entitlements', platformSaasBillingSubscriptionService.getTenantEffectiveEntitlements(tenantId)],
      ['usage', platformSaasBillingSubscriptionService.getTenantSaasUsage(tenantId)],
      ['lifecycle', platformSaasBillingSubscriptionService.getTenantSaasLifecycle(tenantId)],
    ];

    const settled = await Promise.allSettled(requests.map(([, request]) => request));

    const responses = {};
    const failures = [];

    settled.forEach((result, index) => {
      const [key] = requests[index];
      if (result.status === 'fulfilled') {
        responses[key] = result.value;
        return;
      }
      failures.push({ key, error: result.reason });
    });

    failures.forEach(({ key, error: requestError }) => {
      console.error(`[PlatformTenantSubscriptionPanel] Falha ao carregar ${key}`, requestError);
    });

    if (responses.plans) {
      const plansRows = Array.isArray(responses.plans?.data) ? responses.plans.data : [];
      setPlans(plansRows);
      setPlansLoadFailed(false);
    } else {
      setPlansLoadFailed(true);
    }

    if (responses.current) {
      setCurrentSubscription(responses.current?.data || null);
    }

    if (responses.history) {
      const historyRows = Array.isArray(responses.history?.data) ? responses.history.data : [];
      setSubscriptionHistory(historyRows);
    }

    if (responses.entitlements) {
      const entitlementsRows = Array.isArray(responses.entitlements?.data) ? responses.entitlements.data : [];
      setEffectiveEntitlements(entitlementsRows);
    }

    if (responses.usage) {
      const usageItems = Array.isArray(responses.usage?.data) ? responses.usage.data : [];
      setUsageMetrics(usageItems);
    }

    if (responses.lifecycle) {
      setLifecycleState(responses.lifecycle?.data || null);
    }

    const criticalFailure = failures.find(({ key }) => key === 'current' || key === 'history');
    if (criticalFailure) {
      setError(getErrorMessage(criticalFailure.error, 'Nao foi possivel carregar os dados principais da assinatura SaaS.'));
    } else {
      setError('');
    }

    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const planSelectOptions = useMemo(
    () =>
      plans
        .filter((plan) => plan?.status !== 'archived')
        .map((plan) => ({
          value: plan.id,
          label: `${plan.code} - ${plan.name}`,
        })),
    [plans],
  );

  const openCreateModal = () => {
    createForm.setFieldsValue({
      plan_id: undefined,
      initial_status: 'trialing',
      billing_day: undefined,
      trial_days: undefined,
    });
    setCreateModalOpen(true);
  };

  const handleCreateSubscription = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);

      await platformSaasBillingSubscriptionService.createTenantSubscription(tenantId, {
        plan_id: values.plan_id,
        initial_status: values.initial_status,
        billing_day: values.billing_day ?? undefined,
        trial_days: values.trial_days ?? undefined,
      });

      message.success('Assinatura criada com sucesso.');
      setCreateModalOpen(false);
      await loadAllData();
    } catch (requestError) {
      if (requestError?.errorFields) {
        return;
      }
      console.error(requestError);
      message.error(getErrorMessage(requestError, 'Nao foi possivel criar a assinatura.'));
    } finally {
      setCreating(false);
    }
  };

  const openChangePlanModal = () => {
    changePlanForm.setFieldsValue({
      new_plan_id: undefined,
    });
    setChangePlanModalOpen(true);
  };

  const handleChangePlan = async () => {
    try {
      const values = await changePlanForm.validateFields();
      setChangingPlan(true);

      await platformSaasBillingSubscriptionService.changeTenantSubscriptionPlan(tenantId, {
        new_plan_id: values.new_plan_id,
      });

      message.success('Plano alterado com sucesso.');
      setChangePlanModalOpen(false);
      await loadAllData();
    } catch (requestError) {
      if (requestError?.errorFields) {
        return;
      }
      console.error(requestError);
      message.error(getErrorMessage(requestError, 'Nao foi possivel alterar o plano.'));
    } finally {
      setChangingPlan(false);
    }
  };

  const openStatusModal = () => {
    statusForm.setFieldsValue({
      new_status: 'active',
    });
    setStatusModalOpen(true);
  };

  const handleChangeStatus = async () => {
    try {
      const values = await statusForm.validateFields();
      setChangingStatus(true);

      await platformSaasBillingSubscriptionService.changeTenantSubscriptionStatus(tenantId, {
        new_status: values.new_status,
      });

      message.success('Status da assinatura atualizado com sucesso.');
      setStatusModalOpen(false);
      await loadAllData();
    } catch (requestError) {
      if (requestError?.errorFields) {
        return;
      }
      console.error(requestError);
      message.error(getErrorMessage(requestError, 'Nao foi possivel atualizar o status da assinatura.'));
    } finally {
      setChangingStatus(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!tenantId) {
      return;
    }

    setCancelling(true);
    try {
      await platformSaasBillingSubscriptionService.cancelTenantSubscription(tenantId);
      message.success('Assinatura cancelada com sucesso.');
      await loadAllData();
    } catch (requestError) {
      console.error(requestError);
      message.error(getErrorMessage(requestError, 'Nao foi possivel cancelar a assinatura.'));
    } finally {
      setCancelling(false);
    }
  };

  const openDatesModal = () => {
    datesForm.setFieldsValue({
      trial_ends_at: parseDateForForm(currentSubscription?.trial_ends_at),
      grace_ends_at: parseDateForForm(currentSubscription?.grace_ends_at),
    });
    setDatesModalOpen(true);
  };

  const handleUpdateDates = async () => {
    try {
      const values = await datesForm.validateFields();
      setUpdatingDates(true);

      const payload = {
        trial_ends_at: serializeDateForApi(values.trial_ends_at, 'trial_ends_at'),
        grace_ends_at: serializeDateForApi(values.grace_ends_at, 'grace_ends_at'),
      };

      console.info('[PlatformTenantSubscriptionPanel] PATCH subscription dates', {
        tenantId,
        payload,
      });

      await platformSaasBillingSubscriptionService.updateTenantSubscriptionDates(tenantId, payload);

      message.success('Datas da assinatura atualizadas com sucesso.');
      setDatesModalOpen(false);
      await loadAllData();
    } catch (requestError) {
      if (requestError?.errorFields) {
        return;
      }
      console.error(requestError);
      message.error(getErrorMessage(requestError, 'Nao foi possivel atualizar as datas da assinatura.'));
    } finally {
      setUpdatingDates(false);
    }
  };

  const historyColumns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => renderSubscriptionStatusTag(value),
    },
    {
      title: 'Plano',
      key: 'plan',
      render: (_, record) => record?.plan_snapshot_json?.code || record?.plan_id || '-',
    },
    {
      title: 'Periodo',
      key: 'period',
      render: (_, record) => `${formatDate(record?.current_period_start)} ate ${formatDate(record?.current_period_end)}`,
    },
    {
      title: 'Corrente',
      dataIndex: 'is_current',
      key: 'is_current',
      render: (value) => (value ? <Tag color="green">SIM</Tag> : <Tag>Nao</Tag>),
    },
    {
      title: 'Provider',
      dataIndex: 'provider_key',
      key: 'provider_key',
      render: (value) => value || '-',
    },
    {
      title: 'Criada em',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => formatDateTime(value),
    },
  ];

  const entitlementsColumns = [
    {
      title: 'feature_code',
      dataIndex: 'feature_code',
      key: 'feature_code',
      render: (value) => value || '-',
    },
    {
      title: 'feature_name',
      dataIndex: 'feature_name',
      key: 'feature_name',
      render: (value) => value || '-',
    },
    {
      title: 'feature_type',
      dataIndex: 'feature_type',
      key: 'feature_type',
      render: (value) => value || '-',
    },
    {
      title: 'bool_value',
      dataIndex: 'bool_value',
      key: 'bool_value',
      render: (value) => (value === null || value === undefined ? '-' : String(Boolean(value))),
    },
    {
      title: 'limit_value',
      dataIndex: 'limit_value',
      key: 'limit_value',
      render: (value) => (value === null || value === undefined ? '-' : String(value)),
    },
  ];

  const usageLimitItems = useMemo(
    () => usageMetrics.filter((item) => item?.feature_type === 'limit'),
    [usageMetrics],
  );

  const usageBooleanItems = useMemo(
    () => usageMetrics.filter((item) => item?.feature_type === 'boolean'),
    [usageMetrics],
  );

  const renderUsageStatusTag = (status) => {
    const normalized = String(status || '').toLowerCase();
    const color = USAGE_STATUS_TAG_COLOR[normalized] || 'default';
    return <Tag color={color}>{normalized || '-'}</Tag>;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) {
      return '-';
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '-';
    }
    return `${numeric.toFixed(2)}%`;
  };

  const usageLimitColumns = [
    {
      title: 'Feature',
      key: 'feature_name',
      render: (_, record) => record?.feature_name || record?.feature_code || '-',
    },
    {
      title: 'Uso atual',
      dataIndex: 'current_usage',
      key: 'current_usage',
      render: (value) => (value === null || value === undefined ? '-' : String(value)),
    },
    {
      title: 'Limite',
      dataIndex: 'limit_value',
      key: 'limit_value',
      render: (value) => (value === null || value === undefined ? 'Ilimitado' : String(value)),
    },
    {
      title: 'Uso %',
      dataIndex: 'usage_percent',
      key: 'usage_percent',
      render: (value) => formatPercent(value),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => renderUsageStatusTag(value),
    },
  ];

  const usageBooleanColumns = [
    {
      title: 'Feature',
      key: 'feature_name',
      render: (_, record) => record?.feature_name || record?.feature_code || '-',
    },
    {
      title: 'Habilitada',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (value, record) => {
        if (record?.status === 'no_subscription') {
          return '-';
        }
        return value ? <Tag color="green">SIM</Tag> : <Tag>Nao</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => renderUsageStatusTag(value),
    },
  ];

  const hasNoSubscriptionUsage = useMemo(
    () => usageMetrics.some((item) => item?.status === 'no_subscription'),
    [usageMetrics],
  );

  const renderLifecycleStatusTag = (status) => {
    const normalized = String(status || '').toLowerCase();
    const color = LIFECYCLE_STATUS_TAG_COLOR[normalized] || 'default';
    return <Tag color={color}>{normalized || '-'}</Tag>;
  };

  if (loading) {
    return (
      <Card title="Assinatura SaaS" style={{ marginTop: 16 }}>
        <div style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin tip="Carregando assinatura SaaS..." />
        </div>
      </Card>
    );
  }

  return (
    <Card title="Assinatura SaaS" style={{ marginTop: 16 }}>
      {error ? (
        <Alert
          type="error"
          showIcon
          message="Erro ao carregar assinatura SaaS"
          description={error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {!plansLoadFailed && planSelectOptions.length === 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Nenhum plano SaaS disponivel"
          description="Crie um plano no Catalogo SaaS para habilitar a criacao de assinaturas."
        />
      ) : null}

      {currentSubscription ? (
        <>
          <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Assinatura corrente ID">{currentSubscription.id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Plano atual">
              {currentSubscription.plan_snapshot_json?.code || currentSubscription.plan_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {renderSubscriptionStatusTag(currentSubscription.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Provider key">{currentSubscription.provider_key || '-'}</Descriptions.Item>
            <Descriptions.Item label="Trial ends at">
              {formatDateTime(currentSubscription.trial_ends_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Grace ends at">
              {formatDateTime(currentSubscription.grace_ends_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Current period start">
              {formatDate(currentSubscription.current_period_start)}
            </Descriptions.Item>
            <Descriptions.Item label="Current period end">
              {formatDate(currentSubscription.current_period_end)}
            </Descriptions.Item>
            <Descriptions.Item label="Billing day">{currentSubscription.billing_day ?? '-'}</Descriptions.Item>
          </Descriptions>

          <Space wrap style={{ marginBottom: 16 }}>
            <Button type="primary" onClick={openChangePlanModal} disabled={planSelectOptions.length === 0}>
              Trocar plano
            </Button>
            <Button onClick={openStatusModal}>Alterar status</Button>
            <Button onClick={openDatesModal}>Editar datas</Button>
            <Popconfirm
              title="Cancelar assinatura corrente"
              description="Confirma o cancelamento da assinatura atual deste tenant?"
              okText="Cancelar assinatura"
              cancelText="Voltar"
              onConfirm={handleCancelSubscription}
              okButtonProps={{ danger: true, loading: cancelling }}
              disabled={cancelling}
            >
              <Button danger loading={cancelling}>Cancelar assinatura</Button>
            </Popconfirm>
            <Button onClick={loadAllData}>Atualizar painel</Button>
          </Space>
        </>
      ) : (
        <Empty
          description="Este tenant ainda nao possui assinatura SaaS local."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ marginBottom: 16 }}
        >
          <Button type="primary" onClick={openCreateModal} disabled={planSelectOptions.length === 0}>
            Criar assinatura
          </Button>
        </Empty>
      )}

      {!currentSubscription && planSelectOptions.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <Button onClick={openCreateModal}>Criar assinatura</Button>
        </div>
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <Card size="small" title="Historico de assinaturas">
            <Table
              rowKey={(record) => record.id}
              dataSource={subscriptionHistory}
              columns={historyColumns}
              pagination={{ pageSize: 5, hideOnSinglePage: true }}
              locale={{ emptyText: 'Nenhuma assinatura encontrada para este tenant.' }}
              size="small"
            />
          </Card>
        </Col>

        <Col xs={24} xl={10}>
          <Card size="small" title="Entitlements efetivos">
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Leitura somente"
              description="Os entitlements mostrados aqui sao informativos no MVP, sem enforcement real de cobranca/uso."
            />
            <Table
              rowKey={(record) => record.feature_code}
              dataSource={effectiveEntitlements}
              columns={entitlementsColumns}
              pagination={{ pageSize: 5, hideOnSinglePage: true }}
              locale={{ emptyText: 'Sem entitlements efetivos para a assinatura corrente.' }}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Uso do plano" style={{ marginTop: 16 }}>
        {hasNoSubscriptionUsage ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Tenant sem assinatura corrente"
            description="Os limites nao puderam ser resolvidos. Crie/ative uma assinatura para visualizar limites efetivos."
          />
        ) : null}

        <Row gutter={[16, 16]}>
          <Col xs={24} xl={14}>
            <Card size="small" title="Limites">
              <Table
                rowKey={(record) => record.feature_code}
                dataSource={usageLimitItems}
                columns={usageLimitColumns}
                pagination={{ pageSize: 5, hideOnSinglePage: true }}
                locale={{ emptyText: 'Sem metricas de limites para este tenant.' }}
                size="small"
              />
            </Card>
          </Col>

          <Col xs={24} xl={10}>
            <Card size="small" title="Features booleanas">
              <Table
                rowKey={(record) => record.feature_code}
                dataSource={usageBooleanItems}
                columns={usageBooleanColumns}
                pagination={{ pageSize: 5, hideOnSinglePage: true }}
                locale={{ emptyText: 'Sem features booleanas mapeadas para uso.' }}
                size="small"
              />
            </Card>
          </Col>
        </Row>
      </Card>

      <Card size="small" title="Lifecycle SaaS" style={{ marginTop: 16 }}>
        {!lifecycleState ? (
          <Empty description="Lifecycle nao disponivel para este tenant." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <>
            {!lifecycleState.is_access_allowed ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Tenant com acesso comercial nao permitido"
                description={lifecycleState.blocking_reason || 'Sem motivo informado.'}
              />
            ) : null}

            {lifecycleState.should_warn ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Tenant com aviso de lifecycle"
                description={lifecycleState.warning_reason || 'Sem motivo informado.'}
              />
            ) : null}

            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Subscription status">
                {lifecycleState.subscription_status || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Access state">
                {renderLifecycleStatusTag(lifecycleState.access_state)}
              </Descriptions.Item>
              <Descriptions.Item label="Acesso permitido">
                {lifecycleState.is_access_allowed ? <Tag color="green">SIM</Tag> : <Tag color="red">NAO</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Trial ends at">
                {formatDateTime(lifecycleState.trial_ends_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Grace ends at">
                {formatDateTime(lifecycleState.grace_ends_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Dias para fim do trial">
                {lifecycleState.days_until_trial_end ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Dias para fim do grace">
                {lifecycleState.days_until_grace_end ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Warning reason">
                {lifecycleState.warning_reason || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Blocking reason">
                {lifecycleState.blocking_reason || '-'}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Card>

      <Modal
        title="Criar assinatura SaaS"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleCreateSubscription}
        okText="Criar assinatura"
        cancelText="Cancelar"
        okButtonProps={{ loading: creating, disabled: planSelectOptions.length === 0 }}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            name="plan_id"
            label="Plano"
            rules={[{ required: true, message: 'Selecione um plano.' }]}
          >
            <Select
              options={planSelectOptions}
              placeholder="Selecione um plano"
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            name="initial_status"
            label="Status inicial"
            rules={[{ required: true, message: 'Selecione o status inicial.' }]}
          >
            <Select options={INITIAL_STATUS_OPTIONS} />
          </Form.Item>

          <Form.Item
            name="billing_day"
            label="Billing day"
            tooltip="Opcional. Se vazio, o backend usa o dia atual limitado a 28."
          >
            <InputNumber min={1} max={28} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="trial_days"
            label="Trial days"
            tooltip="Opcional. Se vazio, o backend usa o trial_days_default do plano."
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Trocar plano da assinatura"
        open={changePlanModalOpen}
        onCancel={() => setChangePlanModalOpen(false)}
        onOk={handleChangePlan}
        okText="Trocar plano"
        cancelText="Cancelar"
        okButtonProps={{ loading: changingPlan, disabled: planSelectOptions.length === 0 }}
        destroyOnClose
      >
        <Form form={changePlanForm} layout="vertical" preserve={false}>
          <Form.Item
            name="new_plan_id"
            label="Novo plano"
            rules={[{ required: true, message: 'Selecione o novo plano.' }]}
          >
            <Select
              options={planSelectOptions}
              placeholder="Selecione o novo plano"
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Alterar status da assinatura"
        open={statusModalOpen}
        onCancel={() => setStatusModalOpen(false)}
        onOk={handleChangeStatus}
        okText="Atualizar status"
        cancelText="Cancelar"
        okButtonProps={{ loading: changingStatus }}
        destroyOnClose
      >
        <Form form={statusForm} layout="vertical" preserve={false}>
          <Form.Item
            name="new_status"
            label="Novo status"
            rules={[{ required: true, message: 'Selecione o novo status.' }]}
          >
            <Select options={STATUS_CHANGE_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Editar datas da assinatura"
        open={datesModalOpen}
        onCancel={() => setDatesModalOpen(false)}
        onOk={handleUpdateDates}
        okText="Salvar datas"
        cancelText="Cancelar"
        okButtonProps={{ loading: updatingDates }}
        destroyOnClose
      >
        <Form form={datesForm} layout="vertical" preserve={false}>
          <Form.Item name="trial_ends_at" label="Trial ends at">
            <DatePicker allowClear showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="grace_ends_at" label="Grace ends at">
            <DatePicker allowClear showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default PlatformTenantSubscriptionPanel;
